import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../../services/apiService';
import { trackBillingAction } from '../../utils/billingAnalytics';

export default function BillingModalCancelConfirm({ summary, source, onSuccess, onBack, onStateConflict, onRouteRetention }) {
  const posthog = usePostHog();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const status = summary?.status;
  const copy = cancelCopyForStatus(status, summary);

  const retentionInterceptOn = (() => {
    try { return posthog?.isFeatureEnabled?.('billing-retention-intercept-enabled'); } catch { return false; }
  })();

  const handleConfirm = async () => {
    if (pending) return;
    if (retentionInterceptOn) {
      onRouteRetention?.();
      return;
    }
    setPending(true);
    setError(null);
    const started = Date.now();

    trackBillingAction(posthog, 'billing_action_clicked', { action: 'cancel', status, source });

    try {
      const idempotencyKey = crypto.randomUUID();
      const body = await apiService.invokeEdgeFunction('billing-action', {
        body: { action: 'cancel' },
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      trackBillingAction(posthog, 'billing_action_completed', {
        action: 'cancel', status, source, duration_ms: Date.now() - started,
      });
      onSuccess?.(body);
    } catch (e) {
      if (e?.status === 409 && e?.code === 'state_conflict') {
        trackBillingAction(posthog, 'billing_action_failed', {
          action: 'cancel', status, source, duration_ms: Date.now() - started, error_code: 'state_conflict',
        });
        onStateConflict?.({ action: 'cancel', args: {} });
        return;
      }
      trackBillingAction(posthog, 'billing_action_failed', {
        action: 'cancel', status, source, duration_ms: Date.now() - started, error_code: e?.code ?? e?.message,
      });
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModalCancelConfirm', action: 'cancel',
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: status } },
      });
      setError(e?.code || e?.message || 'cancel_failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <View style={styles.root}>
      <Text style={styles.title}>{copy.title}</Text>
      <Text style={styles.body}>{copy.body}</Text>

      {error && <Text role="alert" style={styles.error}>We could not complete the cancellation. Please try again.</Text>}

      <View style={styles.btnRow}>
        <Pressable
          accessibilityRole="button"
          onPress={onBack}
          style={({ pressed }) => [styles.btn, styles.secondary, pressed && { opacity: 0.7 }]}
        >
          <Text style={[styles.btnText, styles.secondaryText]}>Keep subscription</Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          onPress={handleConfirm}
          disabled={pending}
          style={({ pressed }) => [styles.btn, styles.destructive, pressed && { opacity: 0.8 }]}
        >
          <Text style={[styles.btnText, styles.destructiveText]}>{pending ? 'Cancelling...' : copy.confirmLabel}</Text>
        </Pressable>
      </View>
    </View>
  );
}

function cancelCopyForStatus(status, summary) {
  const endDate = summary?.nextEventDate ? new Date(summary.nextEventDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  switch (status) {
    case 'active':
      if (endDate) {
        return {
          title: 'Cancel at end of billing cycle',
          body: `You will keep Pro access until ${endDate}. After that, your subscription ends and billing stops.`,
          confirmLabel: 'Cancel subscription',
        };
      }
      return {
        title: 'Cancel at end of billing cycle',
        body: 'You will keep Pro access until the end of your current billing cycle. After that, your subscription ends and billing stops.',
        confirmLabel: 'Cancel subscription',
      };
    case 'past_due':
      return {
        title: 'Cancel now',
        body: 'Because your last payment did not go through, cancellation takes effect immediately. We will not retry payment.',
        confirmLabel: 'Cancel now',
      };
    case 'paused':
      return {
        title: 'Cancel now',
        body: 'Your subscription is currently paused. Cancelling will end it immediately. You can resubscribe at any time.',
        confirmLabel: 'Cancel now',
      };
    case 'trial_active':
      return {
        title: 'End trial',
        body: 'Your trial will end immediately and Pro access will be removed. You can start a new subscription at any time.',
        confirmLabel: 'End trial',
      };
    case 'trial_carded':
      // Step 8: a carded user has a card on file but has not been charged yet
      // (first charge is deferred to day 7). Cancelling ends the trial now, with
      // no charge. The backend billing-action carded branch cancels the Paddle
      // trialing sub immediately and skips any refund path (there is nothing to
      // refund). Pro access is removed right away.
      return {
        title: 'Cancel before your first charge',
        body: 'Your trial ends immediately and Pro access is removed. You are not charged, and no payment is taken. You can start a new subscription at any time.',
        confirmLabel: 'Cancel trial',
      };
    default:
      return {
        title: 'Cancel subscription',
        body: 'You can resubscribe at any time.',
        confirmLabel: 'Confirm',
      };
  }
}

const styles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 18, gap: 12 },
  title: { fontSize: 17, fontWeight: '600', color: '#FFFFFF' },
  body: { fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 20 },
  btnRow: { flexDirection: 'row', gap: 10, marginTop: 6 },
  btn: { flex: 1, paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  secondary: { backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)' },
  destructive: { backgroundColor: '#991b1b' },
  btnText: { fontSize: 14, fontWeight: '600' },
  secondaryText: { color: '#FFFFFF' },
  destructiveText: { color: '#FFFFFF' },
  error: { fontSize: 13, color: '#FF6B6B' },
});
